import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import { CheckCircle, XCircle, RefreshCw, X } from 'lucide-react';
import { useNotify } from 'react-admin';
import { PromptService } from '../services/promptService';
import {
  generateMarkdownReport,
  downloadMarkdownReport,
} from '../utils/reportGenerator';
import type { ModelComparisonReport, ModelComparisonResult } from '../types';

interface ModelComparisonDialogProps {
  open: boolean;
  onClose: () => void;
  promptId: string;
  promptTemplate: string;
  parameters: Record<string, unknown>;
  sectionTitle: string;
  availableModels: Array<{ key: string; name: string }>;
}

type ModelState = 'pending' | 'running' | 'success' | 'error' | 'retrying';

interface ModelStatus {
  modelKey: string;
  modelName: string;
  state: ModelState;
  result?: ModelComparisonResult;
}

export const ModelComparisonDialog: React.FC<ModelComparisonDialogProps> = ({
  open,
  onClose,
  promptId,
  promptTemplate,
  parameters,
  sectionTitle,
  availableModels,
}) => {
  const notify = useNotify();

  // Selection state
  const [selectedModels, setSelectedModels] = useState<string[]>([]);

  // Execution state
  const [isRunning, setIsRunning] = useState(false);
  const [modelStatuses, setModelStatuses] = useState<Map<string, ModelStatus>>(
    new Map()
  );

  // Report state
  const [report, setReport] = useState<ModelComparisonReport | null>(null);
  const [isComplete, setIsComplete] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedModels([]);
      setIsRunning(false);
      setModelStatuses(new Map());
      setReport(null);
      setIsComplete(false);
    }
  }, [open]);

  // Handle model selection toggle
  const handleToggleModel = (modelKey: string) => {
    if (isRunning) return; // Don't allow changes while running

    setSelectedModels(prev => {
      if (prev.includes(modelKey)) {
        return prev.filter(key => key !== modelKey);
      } else {
        return [...prev, modelKey];
      }
    });
  };

  // Handle select all / deselect all
  const handleToggleAll = () => {
    if (isRunning) return;

    if (selectedModels.length === availableModels.length) {
      setSelectedModels([]);
    } else {
      setSelectedModels(availableModels.map(m => m.key));
    }
  };

  // Run comparison for all selected models
  const handleRun = async () => {
    if (selectedModels.length === 0) {
      notify('Please select at least one model to compare', { type: 'error' });
      return;
    }

    setIsRunning(true);
    setIsComplete(false);

    // Initialize model statuses as 'running'
    const initialStatuses = new Map<string, ModelStatus>();
    for (const modelKey of selectedModels) {
      const model = availableModels.find(m => m.key === modelKey);
      initialStatuses.set(modelKey, {
        modelKey,
        modelName: model?.name || modelKey,
        state: 'running',
      });
    }
    setModelStatuses(initialStatuses);

    // Run each model comparison and update status immediately
    const results: ModelComparisonResult[] = [];
    const promises = selectedModels.map(async modelKey => {
      const model = availableModels.find(m => m.key === modelKey);
      const modelName = model?.name || modelKey;
      const startTime = Date.now();

      try {
        const response = await PromptService.testPrompt(
          promptId,
          promptTemplate,
          modelKey,
          parameters,
          notify
        );

        const responseTime = Date.now() - startTime;
        const result: ModelComparisonResult = {
          modelKey,
          modelName,
          success: response.success,
          result: response.result,
          error: response.error,
          responseTime,
        };

        // Update status immediately
        setModelStatuses(prev => {
          const updated = new Map(prev);
          updated.set(modelKey, {
            modelKey,
            modelName,
            state: response.success ? 'success' : 'error',
            result,
          });
          return updated;
        });

        return result;
      } catch (error) {
        const responseTime = Date.now() - startTime;
        const result: ModelComparisonResult = {
          modelKey,
          modelName,
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
          responseTime,
        };

        // Update status immediately
        setModelStatuses(prev => {
          const updated = new Map(prev);
          updated.set(modelKey, {
            modelKey,
            modelName,
            state: 'error',
            result,
          });
          return updated;
        });

        return result;
      }
    });

    try {
      const allResults = await Promise.all(promises);
      results.push(...allResults);

      // Create final report
      const comparisonReport: ModelComparisonReport = {
        timestamp: new Date().toISOString(),
        promptTemplate,
        parameters,
        sectionTitle,
        results,
        promptId,
      };

      setReport(comparisonReport);
      setIsComplete(true);
    } catch (error) {
      notify(
        `Comparison failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { type: 'error' }
      );
    } finally {
      setIsRunning(false);
    }
  };

  // Retry a single failed model
  const handleRetry = async (modelKey: string) => {
    const status = modelStatuses.get(modelKey);
    if (!status) return;

    // Update state to retrying
    setModelStatuses(prev => {
      const updated = new Map(prev);
      updated.set(modelKey, {
        ...status,
        state: 'retrying',
      });
      return updated;
    });

    const startTime = Date.now();

    try {
      // Run comparison for just this one model
      const response = await PromptService.testPrompt(
        promptId,
        promptTemplate,
        modelKey,
        parameters,
        notify
      );

      const responseTime = Date.now() - startTime;
      const retryResult: ModelComparisonResult = {
        modelKey,
        modelName: status.modelName,
        success: response.success,
        result: response.result,
        error: response.error,
        responseTime,
      };

      // Update the status with the retry result
      setModelStatuses(prev => {
        const updated = new Map(prev);
        updated.set(modelKey, {
          modelKey,
          modelName: status.modelName,
          state: response.success ? 'success' : 'error',
          result: retryResult,
        });
        return updated;
      });

      // Update the report with the new result
      if (report) {
        const updatedResults = report.results.map(r =>
          r.modelKey === modelKey ? retryResult : r
        );
        setReport({
          ...report,
          results: updatedResults,
        });
      }

      if (response.success) {
        notify(`Retry successful for ${status.modelName}`, { type: 'success' });
      } else {
        notify(`Retry failed for ${status.modelName}: ${response.error}`, {
          type: 'error',
        });
      }
    } catch (error) {
      notify(
        `Retry failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { type: 'error' }
      );

      // Revert to error state
      setModelStatuses(prev => {
        const updated = new Map(prev);
        updated.set(modelKey, {
          ...status,
          state: 'error',
        });
        return updated;
      });
    }
  };

  // Download report as markdown
  const handleDownload = () => {
    if (!report) return;

    const markdownContent = generateMarkdownReport(report);
    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, '-')
      .slice(0, -5);
    const filename = `model-comparison-${report.promptId || 'unknown'}-${timestamp}.md`;

    downloadMarkdownReport(markdownContent, filename);
    notify('Report downloaded successfully', { type: 'success' });
  };

  // Render status icon for a model
  const renderStatusIcon = (status: ModelStatus) => {
    switch (status.state) {
      case 'pending':
      case 'running':
      case 'retrying':
        return <CircularProgress size={20} />;
      case 'success':
        return <CheckCircle size={20} color="green" />;
      case 'error':
        return <XCircle size={20} color="red" />;
      default:
        return <CircularProgress size={20} />;
    }
  };

  // Get status text for a model
  const getStatusText = (status: ModelStatus): string | undefined => {
    if (status.result) {
      if (status.result.success) {
        const seconds = Math.round(status.result.responseTime / 1000);
        return `${seconds}s`;
      }
      return status.result.error;
    }

    return undefined;
  };

  const allSelected =
    selectedModels.length === availableModels.length &&
    availableModels.length > 0;

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent closing on backdrop click or escape key
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        onClose();
      }}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        Compare Models
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={isRunning}
          size="small"
          sx={{ ml: 2 }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Select models to compare responses for the same prompt and
            parameters.
          </Typography>
        </Box>

        {!isRunning && !isComplete && (
          <>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Typography variant="subtitle2">Available Models</Typography>
              <Button
                size="small"
                onClick={handleToggleAll}
                disabled={availableModels.length === 0}
              >
                {allSelected ? 'Deselect All' : 'Select All'}
              </Button>
            </Box>

            <FormGroup>
              {availableModels.map(model => (
                <FormControlLabel
                  key={model.key}
                  control={
                    <Checkbox
                      checked={selectedModels.includes(model.key)}
                      onChange={() => handleToggleModel(model.key)}
                    />
                  }
                  label={model.name}
                />
              ))}
            </FormGroup>
          </>
        )}

        {(isRunning || isComplete) && (
          <List dense disablePadding>
            {Array.from(modelStatuses.values()).map(status => (
              <ListItem
                key={status.modelKey}
                dense
                sx={{ py: 0.5 }}
                secondaryAction={
                  status.state === 'error' ? (
                    <IconButton
                      edge="end"
                      onClick={() => handleRetry(status.modelKey)}
                      disabled={status.state === 'retrying'}
                      size="small"
                    >
                      <RefreshCw size={18} />
                    </IconButton>
                  ) : null
                }
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {renderStatusIcon(status)}
                </ListItemIcon>
                <ListItemText
                  primary={status.modelName}
                  secondary={getStatusText(status)}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        {!isComplete && (
          <Button
            onClick={handleRun}
            variant="contained"
            disabled={selectedModels.length === 0 || isRunning}
          >
            {isRunning ? 'Running...' : 'Run'}
          </Button>
        )}

        {isComplete && report && (
          <Button onClick={handleDownload} variant="contained" color="primary">
            Download Report
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
